import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

const JsonContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  overflow: 'auto',
  maxHeight: '600px',
  '& pre': {
    margin: 0,
    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
  '& .json-key': {
    color: theme.palette.mode === 'dark' ? '#9cdcfe' : '#0451a5',
    fontWeight: 500,
  },
  '& .json-value-string': {
    color: theme.palette.mode === 'dark' ? '#ce9178' : '#a31515',
  },
  '& .json-value-number': {
    color: theme.palette.mode === 'dark' ? '#b5cea8' : '#098658',
  },
  '& .json-value-boolean': {
    color: theme.palette.mode === 'dark' ? '#569cd6' : '#0000ff',
  },
  '& .json-value-null': {
    color: theme.palette.mode === 'dark' ? '#569cd6' : '#0000ff',
  },
  '& mark.json-highlight': {
    backgroundColor: theme.palette.mode === 'dark' ? '#665c00' : '#ffe082',
    color: 'inherit',
    padding: '0 1px',
    borderRadius: 2,
  },
}));

interface JsonViewerProps {
  data: any;
  compact?: boolean;
  highlight?: string;
}

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const JsonViewer: React.FC<JsonViewerProps> = ({ data, compact = false, highlight }) => {
  const syntaxHighlight = (json: string): string => {
    return json
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(
        /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
        (match) => {
          let cls = 'json-value-number';
          if (/^"/.test(match)) {
            if (/:$/.test(match)) {
              cls = 'json-key';
            } else {
              cls = 'json-value-string';
            }
          } else if (/true|false/.test(match)) {
            cls = 'json-value-boolean';
          } else if (/null/.test(match)) {
            cls = 'json-value-null';
          }
          return `<span class="${cls}">${match}</span>`;
        }
      );
  };

  const renderContent = () => {
    try {
      const jsonString = JSON.stringify(data, null, compact ? 0 : 2);
      const query = (highlight || '').trim();
      if (query.length >= 2) {
        const escaped = escapeHtml(jsonString);
        const marked = escaped.replace(
          new RegExp(escapeRegExp(escapeHtml(query)), 'gi'),
          (match) => `<mark class="json-highlight">${match}</mark>`
        );
        return <pre dangerouslySetInnerHTML={{ __html: marked }} />;
      }
      const highlighted = syntaxHighlight(jsonString);
      return <pre dangerouslySetInnerHTML={{ __html: highlighted }} />;
    } catch (error) {
      return (
        <Typography color="error" variant="body2">
          Error rendering JSON: {error instanceof Error ? error.message : 'Unknown error'}
        </Typography>
      );
    }
  };

  return <JsonContainer>{renderContent()}</JsonContainer>;
};
